/**
 * Admin-console API client (Sprint 7 — OIDC/BFF).
 *
 * Tenant + roles no longer come from headers; they come from the
 * verified session cookie. The admin app must ensure the user has
 * already authenticated against Keycloak — see
 * `apps/admin/src/session/useSession.ts`.
 */

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

export class ApiError extends Error {
  /**
   * Sprint 31 (GT-TENANT-ADMIN-01) — carry the `x-request-id`
   * header from the failed response so error UIs can surface it
   * for support correlation. Mirrors the same field on the web
   * app's `ApiError` (Sprint 16 Wave H).
   */
  readonly requestId: string | undefined;

  constructor(
    readonly status: number,
    readonly url: string,
    readonly statusText: string,
    readonly body: unknown,
    requestId?: string,
  ) {
    super(`${status} ${statusText} — ${url}`);
    this.name = "ApiError";
    this.requestId = requestId;
  }
}

export class UnauthenticatedError extends ApiError {
  constructor(url: string, body?: unknown, requestId?: string) {
    super(401, url, "Unauthorized", body, requestId);
    this.name = "UnauthenticatedError";
  }
}

/**
 * GT-UI-API-RATE-LIMIT-REQUEST-STORM-FIX-02 — typed HTTP 429 (mirror of
 * the web client). Lets the admin QueryClient retry policy wait for the
 * server's `Retry-After` window instead of retrying immediately.
 */
export class RateLimitError extends ApiError {
  /** Seconds to wait (parsed from `Retry-After`); `null` when absent. */
  readonly retryAfterSeconds: number | null;
  constructor(url: string, retryAfterSeconds: number | null, body?: unknown, requestId?: string) {
    super(429, url, "Too Many Requests", body, requestId);
    this.name = "RateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Parse an HTTP `Retry-After` header into whole seconds (delta-seconds
 * or HTTP-date per RFC 7231 §7.1.3). `null` when absent/unparseable, `0`
 * for a past date, clamped to a 1-hour ceiling. `now` is injectable.
 */
export function parseRetryAfterSeconds(
  headerValue: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (headerValue == null) return null;
  const trimmed = headerValue.trim();
  if (trimmed === "") return null;
  const MAX = 3600;
  if (/^\d+$/.test(trimmed)) {
    const secs = Number(trimmed);
    return Number.isFinite(secs) ? Math.min(secs, MAX) : null;
  }
  const when = Date.parse(trimmed);
  if (Number.isFinite(when)) {
    const secs = Math.ceil((when - now) / 1000);
    return Math.min(Math.max(secs, 0), MAX);
  }
  return null;
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
  const base = API_BASE_URL.replace(/\/+$/, "");
  const url = new URL(`${base}${path.startsWith("/") ? path : `/${path}`}`, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function parseErrorBody(res: Response): Promise<unknown> {
  try {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
}

async function send<T>(url: string, init: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const body = await parseErrorBody(res);
    // Sprint 31 — capture `x-request-id` so error states can show
    // it for support correlation.
    const requestId = res.headers.get("x-request-id") ?? undefined;
    if (res.status === 401) throw new UnauthenticatedError(url, body, requestId);
    if (res.status === 429) {
      throw new RateLimitError(
        url,
        parseRetryAfterSeconds(res.headers.get("retry-after")),
        body,
        requestId,
      );
    }
    throw new ApiError(res.status, url, res.statusText, body, requestId);
  }
  return (await res.json()) as T;
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
  signal?: AbortSignal,
): Promise<T> {
  const url = buildUrl(path, params);
  const init: RequestInit = {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "include",
  };
  if (signal) init.signal = signal;
  return send<T>(url, init);
}

export async function apiSend<T>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const url = buildUrl(path);
  const init: RequestInit = {
    method,
    headers: {
      accept: "application/json",
      ...(body === undefined ? {} : { "content-type": "application/json" }),
    },
    credentials: "include",
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;
  return send<T>(url, init);
}

/**
 * SessionStorage flag set immediately before navigating to the
 * BFF /v1/auth/login. After the user comes back from Keycloak via
 * /v1/auth/callback, the SessionProvider reads this flag and applies
 * a single-retry grace period on its initial /v1/auth/session fetch
 * to absorb any cookie-propagation race (Sprint 47.1, Owner-walkthrough
 * Punkt 14: "/admin/ Login flaky (OIDC race)").
 */
export const POST_LOGIN_GRACE_FLAG = "gt.session.post-login-grace";

export function redirectToLogin(returnTo: string = window.location.href): void {
  try {
    window.sessionStorage.setItem(POST_LOGIN_GRACE_FLAG, "1");
  } catch {
    // SessionStorage may be unavailable (private mode, quota). Skip
    // the grace flag — initial fetch still works, just without retry.
  }
  const base = API_BASE_URL.replace(/\/+$/, "");
  const url = new URL(`${base}/v1/auth/login`, window.location.origin);
  url.searchParams.set("return_to", returnTo);
  window.location.assign(url.toString());
}

export interface SessionInfo {
  identity: { sub: string; email: string; displayName: string | null };
  tenantId: string;
  roles: string[];
  expiresAt: string;
}

export async function fetchSession(signal?: AbortSignal): Promise<SessionInfo | null> {
  try {
    return await apiGet<SessionInfo>("/v1/auth/session", undefined, signal);
  } catch (err) {
    if (err instanceof UnauthenticatedError) return null;
    throw err;
  }
}

/**
 * Sprint 47.1 — Owner-walkthrough Punkt 14 ("/admin/ Login flaky (OIDC
 * race)"). When the user returns from Keycloak via the BFF callback,
 * the session cookie is set on the redirect response, but a sporadic
 * race occasionally lets the first /v1/auth/session fetch race ahead
 * of the cookie being written into the document jar. The result was
 * a redirect loop: 401 → /v1/auth/login → Keycloak SSO already
 * authenticated → bounce back to /admin/ → 401 again, etc.
 *
 * This wrapper applies a single 500ms-delayed retry only when the
 * caller indicates we are within the post-login grace window
 * (via the `POST_LOGIN_GRACE_FLAG` sessionStorage key, written by
 * `redirectToLogin`). The grace flag is consumed (cleared) on the
 * first call so a second 401 does not retry again — preventing any
 * silent infinite loop. If sessionStorage is unavailable, behaviour
 * falls back to the legacy single-fetch path.
 */
export async function fetchSessionWithPostLoginGrace(
  signal?: AbortSignal,
): Promise<SessionInfo | null> {
  const first = await fetchSession(signal);
  if (first) return first;

  let inGrace: boolean;
  try {
    inGrace = window.sessionStorage.getItem(POST_LOGIN_GRACE_FLAG) === "1";
    if (inGrace) window.sessionStorage.removeItem(POST_LOGIN_GRACE_FLAG);
  } catch {
    return null;
  }
  if (!inGrace) return null;

  await new Promise<void>((resolve) => setTimeout(resolve, 500));
  if (signal?.aborted) return null;
  return fetchSession(signal);
}

/**
 * Posts to /v1/auth/logout and returns the IdP end-session URL.
 *
 * Sprint 24.1 Hotfix PR-H1 — accepts an optional `returnTo` URL so
 * the Admin Console can land back on `/admin/` after Keycloak's
 * RP-Initiated Logout (instead of falling through to the Main App
 * default). The server validates the candidate against its
 * `OIDC_POST_LOGIN_REDIRECT_BASES` allow-list (origin-match), so
 * cross-origin values are silently downgraded to the existing
 * default. No env var or Keycloak realm change required: the same
 * origin is already allow-listed by virtue of being the login base.
 */
export async function logout(returnTo?: string): Promise<string> {
  const res = await apiSend<{ endSessionUrl: string }>(
    "POST",
    "/v1/auth/logout",
    returnTo ? { returnTo } : undefined,
  );
  return res.endSessionUrl;
}
